import validator from 'validator';
import { MAX_CART_QUANTITY } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/constants';
import {
  selectSalesPortalCartItem,
  useCartStore,
} from '@/experiences/sales-portal/domain/ordering/blocs/stores/cart-store';
import { useCallback, useState } from 'react';

interface UseCartQuantityInputParams {
  disabled?: boolean;
  /** Receives the validated next quantity. The caller curries any
   *  variantPackUUID it needs, so this hook stays decoupled from the
   *  cart-store's write API. */
  onUpdateQuantity: (quantity: number) => boolean;
  saveCart: () => Promise<unknown>;
  variantPackUUID: string;
}

interface CartQuantityInputBindings {
  onChangeQuantity: (text: string) => void;
  onSaveQuantity: () => void;
  quantity: string;
}

/**
 * Wires a presentational {@link QuantityInput} to the sales-portal cart
 * store. Reads the saved quantity for `variantPackUUID`, validates typed
 * input as a decimal, forwards the new value to `onUpdateQuantity`, and
 * persists on blur — clamped to MAX_CART_QUANTITY, and only when the value
 * has actually changed (`isDirty`).
 *
 * Writes go through the supplied `onUpdateQuantity` callback rather than
 * reaching into `useCartStore.getState()` directly. The caller (typically
 * a card) curries `updateCartItemQuantity` by `variantPackUUID`, so the
 * input chain is fully prop-driven for mutations. The read subscription
 * still uses `variantPackUUID` to select the saved quantity.
 *
 * @param params.disabled - When true, typed changes are ignored (the input
 *   itself stays editable so external-keyboard Tab navigation still lands).
 * @param params.onUpdateQuantity - Curried cart-store write — receives the
 *   validated next quantity.
 * @param params.saveCart - Mutator that persists the cart on blur.
 * @param params.variantPackUUID - The cart line item to read.
 * @returns Bindings for `QuantityInput` — `quantity` (string for empty
 *   state), `onChangeQuantity`, and `onSaveQuantity`.
 */
export const useCartQuantityInput = ({
  disabled,
  onUpdateQuantity,
  saveCart,
  variantPackUUID,
}: UseCartQuantityInputParams): CartQuantityInputBindings => {
  const [isDirty, setIsDirty] = useState(false);

  const savedQuantity = useCartStore(
    (state) => selectSalesPortalCartItem(state, variantPackUUID)?.quantity ?? 0,
  );

  const onChangeQuantity = useCallback(
    (text: string) => {
      if (disabled) {
        return;
      }
      if (!validator.isDecimal(text) && text !== '') {
        return;
      }
      const next = text === '' ? 0 : validator.toFloat(text);
      if (next === savedQuantity) {
        return;
      }
      if (onUpdateQuantity(next)) {
        setIsDirty(true);
      }
    },
    [disabled, onUpdateQuantity, savedQuantity],
  );

  const onSaveQuantity = useCallback(() => {
    if (isDirty) {
      if (savedQuantity > MAX_CART_QUANTITY) {
        onUpdateQuantity(MAX_CART_QUANTITY);
      }
      setIsDirty(false);
      void saveCart();
    }
  }, [isDirty, onUpdateQuantity, saveCart, savedQuantity]);

  return {
    onChangeQuantity,
    onSaveQuantity,
    quantity: savedQuantity === 0 ? '' : savedQuantity.toString(),
  };
};
